import dayjs, { Dayjs } from 'dayjs';

import type { IStartWindow, TicketPeriod } from '../types';

const ISO = 'YYYY-MM-DD';

/** Normalize any date-like input to a midnight Dayjs (drops time). */
const day = (d: string | Dayjs): Dayjs => dayjs(dayjs(d).format(ISO));

/**
 * Preset start dates from the server-evaluated window (§9):
 * `earliest`, `earliest` + 1; `latest`. Dates past `latest`
 * and duplicates are dropped. Empty when the window has no bounds.
 */
export const buildStartDates = (
	window: IStartWindow | undefined,
	period: TicketPeriod
): string[] => {
	if (!window?.earliest || !window.latest) return [];

	const earliest = day(window.earliest);
	const latest = day(window.latest);
	const candidates = [earliest, earliest.add(1, period)];
	if (period === 'month') candidates.push(latest);

	return [
		...new Set(
			candidates
				.filter((date) => !date.isAfter(latest))
				.map((date) => date.format(ISO))
		),
	];
};

/** End-of-validity DATE (the time-of-day lives in validity.validUntil). §9 algorithm. */
export const computeValidityEndDate = (
	startDate: string,
	period: TicketPeriod,
	periodCount?: number
): string => {
	const count = periodCount ?? 1;
	const start = day(startDate);
	const end =
		period === 'day'
			? start.add(count, 'day')
			: period === 'week'
			? start.add(7 * count, 'day')
			: start.add(count, 'month');
	return end.format(ISO);
};

/**
 * Is `date` a valid start date for the picker, given the server window?
 * - within [earliest, latest]
 * - `period_start` alignment: week → Mondays only, month → 1st only
 */
export const isSelectableStartDate = (
	date: string | Dayjs,
	opts: { period: TicketPeriod; window: IStartWindow }
): boolean => {
	const { earliest, latest, alignment } = opts.window;
	if (!earliest || !latest) return false;

	const d = day(date);
	if (d.isBefore(day(earliest)) || d.isAfter(day(latest))) return false;
	if (alignment !== 'period_start') return true;
	if (opts.period === 'week') return d.day() === 1; // Monday
	if (opts.period === 'month') return d.date() === 1; // 1st
	return true; // day
};

/** Inclusive 7-day range (start … start + 6) to highlight in the weekly picker. */
export const weekHighlightRange = (
	start: string | Dayjs
): { start: string; end: string } => {
	const m = day(start);
	return { start: m.format(ISO), end: m.add(6, 'day').format(ISO) };
};
